import type { OptionData } from '@atlaskit/user-picker/types';

import { confluenceSessionStorageInstance as sessionStorageManager } from '@confluence/storage-manager/entry-points/storageInstance.ts';

export const APPROVALS_PANE_ID = 'approvals-pane';
export const APPROVALS_PANE_WIDTH = 400;
export const APPROVALS_PANE_CONTEXT_KEY = 'confluence.approvals.pane.contextKey';
export const APPROVALS_PANE_INITIAL_APPROVERS_KEY = 'confluence.approvals.pane.initialApprovers';
export const APPROVALS_PANE_ENTRY_POINT_KEY = 'confluence.approvals.pane.entryPoint';
export const APPROVALS_PANE_CURRENT_CONTENT_STATUS = 'CURRENT';
export const APPROVALS_PANE_DRAFT_CONTENT_STATUS = 'DRAFT';

export const APPROVALS_PANE_ENTRY_POINTS = [
	'byline',
	'bylineLiveDocConversion',
	'shareDialog',
	'detailsPanel',
	'detailsPanelLiveDocConversion',
	'overflowMenu',
	'editorBanner',
] as const;
export type ApprovalsPaneEntryPoint = (typeof APPROVALS_PANE_ENTRY_POINTS)[number];

export type ApprovalsPaneContext = {
	contentId: string;
	contentStatus: string;
};

type ApprovalsPaneOpenContext = ApprovalsPaneContext & {
	initialApprovers?: OptionData[];
	entryPoint?: ApprovalsPaneEntryPoint;
	rightSidebarContext: {
		open: (panelId: string, width: number, isMinimized?: boolean, toTop?: boolean) => void;
	};
};

type ApprovalsPaneSyncContext = {
	isApprovalsExperimentEnabled: boolean;
	isSidebarApprovalsPaneOpen: boolean;
	contextKey: string;
	closeApprovalsPane: () => void;
};

type ShouldRenderApprovalsPaneArgs = {
	isApprovalsExperimentEnabled: boolean;
	isSidebarApprovalsPaneOpen: boolean;
	contextKey: string;
};

export const getApprovalsPaneContextKey = ({
	contentId,
	contentStatus,
}: ApprovalsPaneContext): string => `${contentId}:${contentStatus}`;

/**
 * In-memory cache of the approvals pane context key.
 *
 * `sessionStorageManager.setItem` delegates to `AtlBrowserStorageSession.setItem`
 * which is **async** (it awaits IndexedDB consent checks before writing to
 * `sessionStorage`). This means a synchronous read immediately after a write
 * will return `null` — the value hasn't been persisted yet.
 *
 * The ownership helpers (`shouldRenderApprovalsPaneForContext`,
 * `syncApprovalsPaneContextOwnership`) run synchronously during React renders
 * and effects, so they need a synchronous source of truth. This in-memory
 * variable serves that purpose. Session storage is still written for
 * durability across page reloads, but in-session reads use this cache.
 */
let inMemoryContextKey: string | null = null;
let inMemoryInitialApprovers: OptionData[] | null = null;
let inMemoryEntryPoint: ApprovalsPaneEntryPoint | null = null;

const setSessionStorageItem = (key: string, value: string): void => {
	sessionStorageManager?.setItem?.(key, value);
};

const removeSessionStorageItem = (key: string): void => {
	sessionStorageManager?.removeItem?.(key);
};

export const getStoredApprovalsPaneContextKey = (): string | null => inMemoryContextKey;

export const setStoredApprovalsPaneContextKey = (contextKey: string): void => {
	inMemoryContextKey = contextKey;
	setSessionStorageItem(APPROVALS_PANE_CONTEXT_KEY, contextKey);
};

export const clearStoredApprovalsPaneContextKey = (): void => {
	inMemoryContextKey = null;
	removeSessionStorageItem(APPROVALS_PANE_CONTEXT_KEY);
	clearApprovalsPaneEntryPoint();
};

export const setApprovalsPaneInitialApprovers = (initialApprovers: OptionData[]): void => {
	inMemoryInitialApprovers = initialApprovers;
	setSessionStorageItem(APPROVALS_PANE_INITIAL_APPROVERS_KEY, JSON.stringify(initialApprovers));
};

export const clearApprovalsPaneInitialApprovers = (): void => {
	inMemoryInitialApprovers = null;
	removeSessionStorageItem(APPROVALS_PANE_INITIAL_APPROVERS_KEY);
};

export const consumeApprovalsPaneInitialApprovers = (): OptionData[] | undefined => {
	const initialApprovers = inMemoryInitialApprovers ?? undefined;
	clearApprovalsPaneInitialApprovers();
	return initialApprovers;
};

export const isApprovalsPaneEntryPoint = (value: unknown): value is ApprovalsPaneEntryPoint =>
	typeof value === 'string' && (APPROVALS_PANE_ENTRY_POINTS as readonly string[]).includes(value);

export const setApprovalsPaneEntryPoint = (entryPoint: ApprovalsPaneEntryPoint): void => {
	inMemoryEntryPoint = entryPoint;
	setSessionStorageItem(APPROVALS_PANE_ENTRY_POINT_KEY, entryPoint);
};

export const clearApprovalsPaneEntryPoint = (): void => {
	inMemoryEntryPoint = null;
	removeSessionStorageItem(APPROVALS_PANE_ENTRY_POINT_KEY);
};

export const consumeApprovalsPaneEntryPoint = (): ApprovalsPaneEntryPoint | undefined => {
	const entryPoint = inMemoryEntryPoint ?? undefined;
	clearApprovalsPaneEntryPoint();
	return entryPoint;
};

export const isApprovalsPaneOwnedByContext = ({
	contextKey,
	storedContextKey,
}: {
	contextKey: string;
	storedContextKey: string | null;
}): boolean => storedContextKey === contextKey;

export const shouldRenderApprovalsPaneForContext = ({
	isApprovalsExperimentEnabled,
	isSidebarApprovalsPaneOpen,
	contextKey,
}: ShouldRenderApprovalsPaneArgs): boolean => {
	if (!isApprovalsExperimentEnabled || !isSidebarApprovalsPaneOpen) {
		return false;
	}

	return isApprovalsPaneOwnedByContext({
		contextKey,
		storedContextKey: getStoredApprovalsPaneContextKey(),
	});
};

export const syncApprovalsPaneContextOwnership = ({
	isApprovalsExperimentEnabled,
	isSidebarApprovalsPaneOpen,
	contextKey,
	closeApprovalsPane,
}: ApprovalsPaneSyncContext): void => {
	if (!isApprovalsExperimentEnabled) {
		return;
	}

	if (!isSidebarApprovalsPaneOpen) {
		clearStoredApprovalsPaneContextKey();
		return;
	}

	const storedContextKey = getStoredApprovalsPaneContextKey();
	if (isApprovalsPaneOwnedByContext({ contextKey, storedContextKey })) {
		return;
	}

	closeApprovalsPane();
	clearStoredApprovalsPaneContextKey();
};

export const openApprovalsPaneForContext = ({
	contentId,
	contentStatus,
	initialApprovers,
	entryPoint,
	rightSidebarContext,
}: ApprovalsPaneOpenContext): void => {
	setStoredApprovalsPaneContextKey(getApprovalsPaneContextKey({ contentId, contentStatus }));
	if (initialApprovers) {
		setApprovalsPaneInitialApprovers(initialApprovers);
	} else {
		clearApprovalsPaneInitialApprovers();
	}
	if (entryPoint) {
		setApprovalsPaneEntryPoint(entryPoint);
	} else {
		clearApprovalsPaneEntryPoint();
	}
	rightSidebarContext.open(APPROVALS_PANE_ID, APPROVALS_PANE_WIDTH);
};
